import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  Box,
  InputAdornment,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import CallIcon from "@mui/icons-material/Call";
import DynamicFormIcon from "@mui/icons-material/DynamicForm";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";

export default function ExecuteFlow({ token }) {
  const [to, setTo] = useState("");
  const [from, setFrom] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(true);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/templates`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          setTemplates(await res.json());
        } else {
          setMessage({ type: "error", text: "Failed to load templates. Please refresh." });
        }
      } catch {
        setMessage({ type: "error", text: "Error loading templates. Check your connection." });
      } finally {
        setTemplatesLoading(false);
      }
    };
    fetchTemplates();
  }, [token]);

  const doExecute = async (e) => {
    e.preventDefault();
    setMessage(null);
    if (!selectedTemplate) { setMessage({ type: "error", text: "Please select a call template." }); return; }
    if (!to || !from) { setMessage({ type: "error", text: "Please fill in all required fields." }); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to, from, flowSid: selectedTemplate, params: {} }),
      });
      if (res.ok) {
        setMessage({ type: "success", text: "Flow executed successfully! Call initiated." });
        setTo(""); setFrom(""); setSelectedTemplate("");
      } else {
        const d = await res.json();
        setMessage({ type: "error", text: d.error || "Execution failed. Please try again." });
      }
    } catch {
      setMessage({ type: "error", text: "Error executing flow. Check your connection." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-wrapper animate-in">
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: "#0f172a", mb: 0.5 }}>
          Execute Flow
        </Typography>
        <Typography sx={{ color: "#64748b", fontSize: "0.95rem" }}>
          Configure and initiate your vishing simulation call
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 3, flexWrap: { xs: "wrap", md: "nowrap" } }}>
        {/* Left - Form */}
        <Box sx={{ flex: "1 1 480px", maxWidth: 560 }}>
          <Box className="enterprise-card" sx={{ p: 4 }}>
            {message && (
              <Alert severity={message.type} sx={{ mb: 3, borderRadius: "10px" }} onClose={() => setMessage(null)}>
                {message.text}
              </Alert>
            )}

            <form onSubmit={doExecute}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                {/* Template */}
                <Box>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#334155", mb: 0.8, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <DynamicFormIcon sx={{ fontSize: 16, color: "#6366f1" }} /> Call Template
                  </Typography>
                  <TextField
                    fullWidth select size="small" required
                    label={templatesLoading ? "Loading templates..." : "Select Template"}
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    disabled={loading || templatesLoading}
                  >
                    <MenuItem value="">Select Template</MenuItem>
                    {templates.map((tpl) => (
                      <MenuItem key={tpl.sid} value={tpl.sid}>{tpl.name}</MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* Phone numbers side by side */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#334155", mb: 0.8, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 16, color: "#6366f1" }} /> Recipient
                    </Typography>
                    <TextField
                      fullWidth size="small" required
                      placeholder="+91 XXXXXXXXXX"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      disabled={loading}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: "#94a3b8", fontSize: 18 }} /></InputAdornment>,
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#334155", mb: 0.8, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 16, color: "#6366f1" }} /> Sender
                    </Typography>
                    <TextField
                      fullWidth size="small" required
                      placeholder="+91 XXXXXXXXXX"
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                      disabled={loading}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: "#94a3b8", fontSize: 18 }} /></InputAdornment>,
                      }}
                    />
                  </Box>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading || templatesLoading || !selectedTemplate || !to || !from}
                  startIcon={loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <RocketLaunchIcon />}
                  sx={{ mt: 1, py: 1.4, fontSize: "0.95rem" }}
                >
                  {loading ? "Initiating Call..." : "Execute Flow"}
                </Button>
              </Box>
            </form>
          </Box>
        </Box>

        {/* Right - Info cards */}
        <Box sx={{ flex: "0 0 280px", display: "flex", flexDirection: "column", gap: 2 }}>
          <Card sx={{ background: "rgba(99,102,241,0.04)", border: "1px solid rgba(99,102,241,0.12)" }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: "8px", background: "rgba(99,102,241,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CallIcon sx={{ color: "#6366f1", fontSize: 18 }} />
                </Box>
                <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#0f172a" }}>
                  Templates Available
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: "#6366f1", fontWeight: 700 }}>
                {templatesLoading ? "—" : templates.length}
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: "0.8rem", mt: 0.5 }}>
                Call flow templates ready to deploy
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ background: "rgba(16,185,129,0.04)", border: "1px solid rgba(16,185,129,0.12)" }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#0f172a", mb: 1 }}>
                How It Works
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {["Select a call flow template", "Enter recipient & sender numbers", "Execute to initiate the call"].map((step, i) => (
                  <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Box sx={{ width: 20, height: 20, minWidth: 20, borderRadius: "50%", background: "#6366f1", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.65rem", fontWeight: 700 }}>
                      {i + 1}
                    </Box>
                    <Typography sx={{ fontSize: "0.8rem", color: "#64748b" }}>{step}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </div>
  );
}
